/**
 * Display helpers shared by the ballot initiative directory and detail pages.
 */

import type { BallotInitiativeListItem, BallotInitiativeDetail, InitiativeTrackingRow } from './types';
import { richTextIsEmpty } from '../../utils/richText';
import { isBallotVisible, TRACKING_POSITIONS } from '../../constants/ballotInitiatives';
import { measureBlueBookUrl } from './blueBook';

interface InitiativeDisplayIdentity {
  popular_name?: string | null;
  ballot_title?: string | null;
  unofficial_caption?: string | null;
  sos_initiative_number?: string | null;
  measure_number?: string | null;
  status?: string | null;
}

/**
 * The assigned ballot designation as voters see it. Colorado assigns
 * "Amendment 81" / "Proposition 127" — a name that already says what it is —
 * so no prefix; a bare number or letter still gets "Ballot" in front.
 */
export function ballotDesignation(measureNumber: string | null | undefined): string | null {
  const value = (measureNumber ?? '').trim();
  if (!value) return null;
  return /^(amendment|proposition|measure|question|referendum|issue|initiative)\b/i.test(value)
    ? value
    : `Ballot ${value}`;
}

/**
 * The one name a measure should be called by, at whatever stage it is in.
 * Pre-ballot measures often have only a staff caption or an SOS filing number.
 */
export function initiativeDisplayName(
  initiative: InitiativeDisplayIdentity
): string {
  return (
    initiative.popular_name ||
    initiative.unofficial_caption ||
    initiative.ballot_title ||
    (initiative.sos_initiative_number ? `Initiative #${initiative.sos_initiative_number}` : null) ||
    ballotDesignation(initiative.measure_number) ||
    'Untitled measure'
  );
}

/**
 * Secondary identifier line: ballot designation and/or SOS filing number.
 * Once a measure is on the ballot, voters know it by its designation, so that
 * leads ("Amendment 81 · Initiative #95"); before that the filing number is the
 * identity people track ("Initiative #95"). (Angie, 2026-09-12.)
 */
export function initiativeNumberLine(
  initiative: Pick<InitiativeDisplayIdentity, 'measure_number' | 'sos_initiative_number' | 'status'>
): string {
  const filing = initiative.sos_initiative_number ? `Initiative #${initiative.sos_initiative_number}` : null;
  const designation = ballotDesignation(initiative.measure_number);
  const onBallot = Boolean(designation) && isBallotVisible(initiative.status ?? '');
  return (onBallot ? [designation, filing] : [filing, designation])
    .filter(Boolean)
    .join(' · ');
}

/** Workspace tracking position, coloured as the workspace tab shows it. The
 *  vocabulary is the API's lowercase set, unlike the capitalised candidate
 *  strings the shared PositionCellRenderer expects. */
export const positionColorClasses = (position: string | null | undefined): string => {
  switch (position) {
    case 'support': return 'bg-success-100 dark:bg-success-900/20 text-success-700 dark:text-success-300';
    case 'oppose': return 'bg-error-100 dark:bg-error-900/20 text-error-700 dark:text-error-300';
    case 'monitor': return 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300';
    default: return 'bg-background-tertiary dark:bg-background-dark-tertiary text-text-secondary dark:text-text-dark-secondary';
  }
};

export const positionLabel = (position: string | null | undefined): string =>
  TRACKING_POSITIONS.find(p => p.value === position)?.label ?? '—';

/** Cycle label, e.g. "2026 CO General" — falls back to the bare year. */
export function cycleLabel(initiative: Pick<BallotInitiativeListItem, 'election_calendar'>): string {
  const cal = initiative.election_calendar;
  if (!cal) return '—';
  return cal.name || (cal.election_year != null ? String(cal.election_year) : '—');
}

/** Normalize exact consecutive-year initiative cycles returned by filter options. */
export function normalizeInitiativeCycles(values: unknown[]): string[] {
  return [...new Set(values
    .map(value => String(value || '').trim())
    .filter((value) => {
      const match = /^(\d{4})-(\d{4})$/.exec(value);
      return Boolean(match && Number(match[2]) === Number(match[1]) + 1);
    }))]
    .sort((a, b) => b.localeCompare(a));
}

/** Select the configured current cycle only when it is present in this dataset. */
export function currentDatasetInitiativeCycle(
  cycles: string[],
  configuredCurrentCycle: unknown
): string {
  const current = String(configuredCurrentCycle || '').trim();
  return cycles.includes(current) ? current : '';
}

/**
 * Format a Postgres DATE (or ISO timestamp) as a local calendar date without the
 * UTC-midnight shift: `2026-11-03` must render as Nov 3 in every timezone.
 */
export function formatCalendarDate(value: string | null | undefined): string {
  if (!value) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.toLocaleDateString();
}

/** Human-readable authority for a lifecycle source link. */
export function initiativeSourceLinkLabel(
  sourceLabel: string | null | undefined,
  sourceUrl: string | null | undefined
): string {
  if (sourceUrl) {
    try {
      const host = new URL(sourceUrl).hostname.toLowerCase();
      if (host.endsWith('sos.state.co.us')) return 'SOS Title Board result';
      if (host.endsWith('leg.colorado.gov')) return 'Legislative Council filing';
    } catch {
      // Retained legacy values may not be absolute URLs; keep the fallback honest.
    }
  }
  if (sourceLabel?.trim()) return sourceLabel.trim();
  return 'Official source';
}

/** The four official-document link fields, wherever a surface offers them. */
interface MeasureDocumentSource {
  full_text_url?: string | null;
  blue_book_url?: string | null;
  election_calendar?: { blue_book_url?: string | null } | null;
  fiscal_impact_url?: string | null;
  ballotpedia_url?: string | null;
}

/**
 * The measure's official document links, labeled once. Three surfaces (the
 * measure hero, the citizen ballot card, the admin detail) grew their own
 * copies of this list with drifting labels and orderings — each surface keeps
 * its own chrome (pill, chip, row), the label and order live here.
 */
export function measureDocumentLinks(measure: MeasureDocumentSource): Array<{ label: string; url: string }> {
  return (
    [
      { label: 'Full Text', url: measure.full_text_url },
      { label: 'Blue Book', url: measureBlueBookUrl(measure) },
      // The state's own document, named for what it is (v2 PRD §5.6.6) — a
      // "fiscal note" is what a bill gets; a measure gets a fiscal impact statement.
      { label: 'State Fiscal Impact Statement', url: measure.fiscal_impact_url },
      { label: 'Ballotpedia', url: measure.ballotpedia_url },
    ] as Array<{ label: string; url: string | null | undefined }>
  ).filter((doc): doc is { label: string; url: string } => Boolean(doc.url));
}

/**
 * Whether the reader sections between the hero and the official record have
 * anything to show. When a measure carries only classification data they all
 * hide themselves, and the page needs an honest empty state instead of
 * looking broken.
 *
 * The list is the union of what those sections render: the hero's summary (and
 * its Show more), what a YES/NO vote means, who's for and against it (staff
 * reasons, sided committees, endorsements, sourced arguments, candidate
 * positions), background, fiscal, and results. The SOS full title, filed text
 * and Blue Book live in the Official record, which always renders (it carries
 * the source updates), so they must NOT count — a measure carrying only those
 * would pass the gate and leave a blank gap where the reader sections go.
 * Rich-text fields are judged by richTextIsEmpty so a Quill-emptied value
 * counts as blank.
 */
export function measureHasDetailContent(initiative: BallotInitiativeDetail): boolean {
  const fiscalEntries = Object.entries(initiative.fiscal_impact_detail ?? {})
    .filter(([, value]) => value != null && String(value).trim() !== '');
  const sidedCommittees = (initiative.committees ?? [])
    .filter((c) => c.committee_type === 'proponent' || c.committee_type === 'opponent');

  return Boolean(
    !richTextIsEmpty(initiative.plain_language_summary) ||
    !richTextIsEmpty(initiative.plain_language_long_description) ||
    !richTextIsEmpty(initiative.what_yes_means) ||
    !richTextIsEmpty(initiative.what_no_means) ||
    !richTextIsEmpty(initiative.background_context) ||
    !richTextIsEmpty(initiative.who_supports_and_why) ||
    !richTextIsEmpty(initiative.who_opposes_and_why) ||
    !richTextIsEmpty(initiative.fiscal_impact_statement) ||
    fiscalEntries.length > 0 ||
    Boolean(initiative.fiscal_impact_url) ||
    (initiative.votes_yes != null && initiative.votes_no != null) ||
    (initiative.arguments?.length ?? 0) > 0 ||
    sidedCommittees.length > 0 ||
    (initiative.endorsements?.length ?? 0) > 0 ||
    (initiative.candidate_positions?.length ?? 0) > 0
  );
}

/**
 * IndexedDB persistence JSON-serializes React Query caches, so tracking rows are
 * cached as a plain array; derive Set/Map views at the call site (the same
 * lesson as candidateTrackedIds.ts).
 */
export function toTrackedInitiativeIdSet(rows: InitiativeTrackingRow[] | undefined): Set<string> {
  return new Set((Array.isArray(rows) ? rows : []).map(r => r.ballot_initiative_id));
}

export function workspacesTrackingInitiative(
  rows: InitiativeTrackingRow[] | undefined,
  initiativeId: string
): InitiativeTrackingRow[] {
  return (Array.isArray(rows) ? rows : []).filter(r => r.ballot_initiative_id === initiativeId);
}
